/**
 * RPC Error Types (Client-safe)
 *
 * These error types are used in RPC definitions and can be imported by clients.
 * They have no server-side dependencies.
 *
 * @since 1.0.0
 */
import { Schema } from "effect";

// ============================================================================
// Authentication Errors
// ============================================================================

/**
 * Unauthorized error for authentication failures
 *
 * Used when:
 * - No authorization header is present
 * - Invalid session token
 * - Token verification fails
 * - User not found in database
 *
 * @since 1.0.0
 */
export class Unauthorized extends Schema.TaggedError<Unauthorized>()(
  "Unauthorized",
  {
    message: Schema.String,
  },
) {}

/**
 * Forbidden error for authorization failures
 *
 * Used when:
 * - User is authenticated but lacks required role(s)
 * - User attempts to access resource they don't have permission for
 *
 * @since 1.0.0
 */
export class Forbidden extends Schema.TaggedError<Forbidden>()(
  "Forbidden",
  {
    message: Schema.String,
    required: Schema.Array(Schema.String),
    actual: Schema.Array(Schema.String),
  },
) {}

/**
 * Clerk error for Clerk API failures
 *
 * Used when:
 * - Failed to fetch user from Clerk API
 * - Clerk API request fails
 *
 * @since 1.0.0
 */
export class ClerkError extends Schema.TaggedError<ClerkError>()(
  "ClerkError",
  {
    message: Schema.String,
  },
) {}

/**
 * Clerk allowlist sync error (CLA-649).
 *
 * Raised by {@link ClerkAllowlistSync} when pushing an expected sign-up email
 * onto — or removing it from — the clawinstall Clerk sign-up allowlist fails
 * against the Clerk backend API. A duplicate-identifier create and a
 * missing-identifier delete are NOT failures (the sync is idempotent), so this
 * only carries genuine Clerk/transport errors. Surfaces fatally on link
 * issuance (a link whose email cannot sign up is worse than a visible error)
 * and non-fatally on the admin-create / erase cleanup paths.
 *
 * @since 1.0.0
 */
export class ClerkAllowlistError extends Schema.TaggedError<ClerkAllowlistError>()(
  "ClerkAllowlistError",
  {
    message: Schema.String,
  },
) {}

/**
 * Raised by `admin/impersonate/start` ("Sign in as user") when the request
 * cannot be turned into a Clerk actor token. Kept distinct from a generic 500 so
 * the admin UI can show a precise, non-alarming message. Covers:
 *
 *  - the acting admin's session carries no `clerkId` — a non-interactive service
 *    token cannot be the actor `sub` of an impersonation,
 *  - the target person has no Clerk account to impersonate,
 *  - the Clerk actor-token API call failed, or returned no `url` to navigate to.
 *
 * @since 1.0.0
 */
export class ImpersonationError extends Schema.TaggedError<ImpersonationError>()(
  "ImpersonationError",
  {
    message: Schema.String,
  },
) {}

// ============================================================================
// Database Errors
// ============================================================================

/**
 * Database error
 * @since 1.0.0
 */
export class DatabaseError extends Schema.TaggedError<DatabaseError>()(
  "DatabaseError",
  {
    code: Schema.optional(Schema.Number),
    message: Schema.optional(Schema.String),
  },
) {}

/**
 * Record not found error
 * @since 1.0.0
 */
export class RecordNotFoundError extends Schema.TaggedError<RecordNotFoundError>()(
  "RecordNotFoundError",
  {
    table: Schema.String,
    where: Schema.Record({ key: Schema.String, value: Schema.Unknown }),
    message: Schema.optional(Schema.String),
  },
) {}

/**
 * Raised when a create flow that mints a brand-new person (intake / customer
 * walk-in) is given a contact email that already belongs to an existing
 * `people` row. `people.email` is unique and an intake is a once-per-person
 * acquisition record, so a collision means the person is already in the system
 * — a CSR mistake rather than a generic database failure. Carries the offending
 * `email` so the UI can name it and point the operator at the existing record.
 *
 * @since 1.0.0
 */
export class PersonAlreadyExistsError extends Schema.TaggedError<PersonAlreadyExistsError>()(
  "PersonAlreadyExistsError",
  {
    email: Schema.String,
    message: Schema.optional(Schema.String),
  },
) {}

/**
 * Remote-support consent refusal (CLA-809).
 *
 * Raised by `guardRemoteDispatch` when an operator dispatch surface (command
 * enqueue, SSH open, force-update push, admin profile publish, migrate) targets
 * an ADOPTED box (`agent_installations.adopted_at` set) whose heartbeat-carried
 * consent record says `remote_support_enabled = false`. Platform boxes
 * (`adopted_at` null) are never refused. Carries the offending
 * `installationId` so the admin UI can point the operator at the box and its
 * remedy: the customer re-enables remote support on the box.
 *
 * @since 1.0.0
 */
export class RemoteSupportNotEnabled extends Schema.TaggedError<RemoteSupportNotEnabled>()(
  "RemoteSupportNotEnabled",
  {
    installationId: Schema.String,
    message: Schema.String,
  },
) {}

/**
 * Machine-readable cause for an {@link OpenclawUpgradeRefused} refusal — the
 * admin `installs.upgradeOpenclaw` / `installs.rollbackOpenclaw` RPCs refuse a
 * canary trigger before anything is enqueued and name why (CLA-993):
 *
 *  - `no-installed-version` — the install has never heartbeated an `ocVersion`,
 *    so there is no floor to upgrade from (or currently-installed version to
 *    roll back).
 *  - `not-an-upgrade` — the requested `targetVersion` is equal to or below the
 *    installed `ocVersion` (a no-op or a downgrade — rollback is the explicit
 *    path for going backwards).
 *  - `refused-target` — the target falls inside the shared refused-upgrade
 *    window (`isRefusedOpenclawUpgradeTarget`), or is unparseable (fail closed).
 *  - `upgrade-in-flight` — the newest `openclaw_upgrade_step` event is newer
 *    than the newest terminal `openclaw_upgrade` outcome, so an upgrade is still
 *    running on the box.
 *  - `no-upgrade-outcome` — rollback found no terminal `openclaw_upgrade`
 *    outcome carrying `from`/`to` versions to reinstate.
 *
 * @since 1.0.0
 */
export const OpenclawUpgradeRefusedReason = Schema.Literal(
  "no-installed-version",
  "not-an-upgrade",
  "refused-target",
  "upgrade-in-flight",
  "no-upgrade-outcome",
);
export type OpenclawUpgradeRefusedReason = Schema.Schema.Type<typeof OpenclawUpgradeRefusedReason>;

/**
 * OpenClaw core upgrade/rollback refusal (CLA-993).
 *
 * Raised by the admin `installs.upgradeOpenclaw` / `installs.rollbackOpenclaw`
 * RPCs when the requested canary trigger fails the shared version policy or the
 * in-flight/rollback preconditions — refused BEFORE any command is enqueued,
 * carrying the offending `installationId`, the `targetVersion` under
 * consideration (empty when rollback found no outcome to name one), and a
 * machine-readable {@link OpenclawUpgradeRefusedReason} so the admin UI can show
 * a precise message.
 *
 * @since 1.0.0
 */
export class OpenclawUpgradeRefused extends Schema.TaggedError<OpenclawUpgradeRefused>()(
  "OpenclawUpgradeRefused",
  {
    installationId: Schema.String,
    targetVersion: Schema.String,
    reason: OpenclawUpgradeRefusedReason,
  },
) {}

/**
 * Validation error for invalid input
 * @since 1.0.0
 */
export class ValidationError extends Schema.TaggedError<ValidationError>()(
  "ValidationError",
  {
    field: Schema.String,
    message: Schema.String,
  },
) {}

/**
 * Raised when an admin tries to set a customer's primary contact to a person
 * who is not a member of that customer. The primary contact must always be one
 * of the customer's `customer_members`, so this is a 422-style domain error
 * rather than a generic validation failure.
 *
 * @since 1.0.0
 */
export class PersonNotMemberError extends Schema.TaggedError<PersonNotMemberError>()(
  "PersonNotMemberError",
  {
    customerId: Schema.String,
    personId: Schema.String,
    message: Schema.optional(Schema.String),
  },
) {}

/**
 * Raised when an admin tries to edit an intake whose lifecycle has advanced
 * past the editable window (`provisioning` or later). Once provisioning has
 * started the acquisition worksheet is frozen, so content edits are a domain
 * error rather than a generic validation failure. Carries the offending `id`
 * and `status` so the UI can show a precise message.
 *
 * @since 1.0.0
 */
export class IntakeNotEditableError extends Schema.TaggedError<IntakeNotEditableError>()(
  "IntakeNotEditableError",
  {
    id: Schema.String,
    status: Schema.String,
    message: Schema.optional(Schema.String),
  },
) {}

/**
 * Invalid state transition error.
 *
 * Returned when an admin action requests a status change that the
 * legal-transitions table does not allow — e.g., re-deleting an
 * already-deleted install. Carries the offending `from`/`to`
 * statuses so the UI can show a precise message.
 *
 * @since 1.0.0
 */
export class InvalidStateTransition extends Schema.TaggedError<InvalidStateTransition>()(
  "InvalidStateTransition",
  {
    table: Schema.String,
    id: Schema.String,
    from: Schema.String,
    to: Schema.String,
    message: Schema.String,
  },
) {}

// ============================================================================
// Onboarding Errors
// ============================================================================

/**
 * Why a customer onboarding magic link could not be resolved.
 *
 *  - `invalid` — no intake matches the supplied token hash.
 *  - `expired` — the link's `linkExpiresAt` is in the past (or absent).
 *  - `resolved` — the link was already consumed (`linkResolvedAt` set), so it
 *    cannot be resolved a second time.
 *
 * @since 1.0.0
 */
export const OnboardingLinkErrorReason = Schema.Literal("invalid", "expired", "resolved");
export type OnboardingLinkErrorReason = Schema.Schema.Type<typeof OnboardingLinkErrorReason>;

/**
 * Raised by the customer-facing onboarding surface when a magic-link token
 * cannot be resolved into a live intake. Carries a machine-readable `reason`
 * so the UI can distinguish "bad link" from "expired" from "already used".
 *
 * @since 1.0.0
 */
export class OnboardingLinkError extends Schema.TaggedError<OnboardingLinkError>()(
  "OnboardingLinkError",
  {
    reason: OnboardingLinkErrorReason,
    message: Schema.String,
  },
) {}

/**
 * Raised by the customer-facing region-update RPC when the caller tries to
 * change a cloud-vps agent's region AFTER its provisioning workflow has already
 * been dispatched (`agent_installations.provisioning_dispatched_at` set). The
 * region is captured at intake, confirmed/changed once on the Install step, and
 * immutable thereafter — the provisioning workflow has already read it, so a
 * later change would silently diverge from the box actually being built
 * (CLA-484).
 *
 * @since 1.0.0
 */
export class ProvisioningAlreadyDispatchedError extends Schema.TaggedError<ProvisioningAlreadyDispatchedError>()(
  "ProvisioningAlreadyDispatchedError",
  {
    agentId: Schema.String,
    message: Schema.String,
  },
) {}

// ============================================================================
// Chat Errors
// ============================================================================

/**
 * Conversation ownership error
 *
 * Used when a user attempts to access a conversation they don't own.
 *
 * @since 1.0.0
 */
export class ConversationOwnershipError extends Schema.TaggedError<ConversationOwnershipError>()(
  "ConversationOwnershipError",
  {
    message: Schema.String,
    conversationId: Schema.String,
    userId: Schema.String,
  },
) {}

/**
 * Conversation not found error
 *
 * Used when a conversation ID doesn't exist in the database.
 *
 * @since 1.0.0
 */
export class ConversationNotFoundError extends Schema.TaggedError<ConversationNotFoundError>()(
  "ConversationNotFoundError",
  {
    message: Schema.String,
    conversationId: Schema.String,
  },
) {}

/**
 * Scene not found error.
 *
 * Raised by `conversation.startScene` when the requested `sceneKey` does not
 * match any module in the scene registry.
 *
 * @since 1.0.0
 */
export class SceneNotFoundError extends Schema.TaggedError<SceneNotFoundError>()(
  "SceneNotFoundError",
  {
    message: Schema.String,
    sceneKey: Schema.String,
  },
) {}

/**
 * Not-a-scene-conversation error.
 *
 * Raised by `conversation.sendScene` when the target conversation is not a
 * scene conversation (its `sceneKey` is null, or names an unknown scene). The
 * scene send path only applies to conversations started via `startScene`.
 *
 * @since 1.0.0
 */
export class NotASceneConversationError extends Schema.TaggedError<NotASceneConversationError>()(
  "NotASceneConversationError",
  {
    message: Schema.String,
    conversationId: Schema.String,
  },
) {}

/**
 * Scene ended error.
 *
 * Raised by `conversation.sendScene` when the scene has already ended — its
 * message budget is spent or a prior turn fired an ending rule. No further user
 * messages are accepted.
 *
 * @since 1.0.0
 */
export class SceneEndedError extends Schema.TaggedError<SceneEndedError>()(
  "SceneEndedError",
  {
    message: Schema.String,
    conversationId: Schema.String,
  },
) {}

/**
 * Scene-not-ended error.
 *
 * Raised by `conversation.reveal` when the caller asks for the Reveal before the
 * scene has ended — no assistant message carries an `endingKind` and the user
 * has not yet spent the scene's message budget. The Reveal is her side of a
 * finished story, so it is refused until the scene is actually over.
 *
 * @since 1.0.0
 */
export class SceneNotEndedError extends Schema.TaggedError<SceneNotEndedError>()(
  "SceneNotEndedError",
  {
    message: Schema.String,
    conversationId: Schema.String,
  },
) {}

// ============================================================================
// Simulator Errors
// ============================================================================

/**
 * Sim-run-not-found error.
 *
 * Raised by the simulator seams (`TurnEngine.sendTurn` / `finishRun`, and the
 * admin `sim.sendTurn` / `sim.finishRun` / `sim.runDetail` RPCs) when a run id
 * does not resolve to a `sim_runs` row. Client-safe so the RPC error union can
 * name it directly.
 *
 * @since 1.0.0
 */
export class SimRunNotFoundError extends Schema.TaggedError<SimRunNotFoundError>()(
  "SimRunNotFoundError",
  {
    message: Schema.String,
    runId: Schema.String,
  },
) {}

/**
 * Archetype-not-found error.
 *
 * Raised by the archetype driver (`SimRunner`) and the admin `sim.startRuns`
 * RPC when an archetype key is not in the registry. Client-safe so the RPC
 * error union can name it directly.
 *
 * @since 1.0.0
 */
export class ArchetypeNotFoundError extends Schema.TaggedError<ArchetypeNotFoundError>()(
  "ArchetypeNotFoundError",
  {
    message: Schema.String,
    archetypeKey: Schema.String,
  },
) {}

// ============================================================================
// Payment/Subscription Errors
// ============================================================================

/**
 * Subscription error code types.
 *
 * @since 1.0.0
 */
export const SubscriptionErrorCode = Schema.Literal(
  "webhook_signature_invalid",
  "webhook_already_processed",
  "subscription_not_found",
  "stripe_api_error",
  "revenuecat_sync_failed",
  "stripe_brand_not_configured",
  "reseller_not_onboarded",
);
export type SubscriptionErrorCode = Schema.Schema.Type<typeof SubscriptionErrorCode>;

/**
 * Subscription error for payment-related failures.
 *
 * Used when:
 * - Webhook signature validation fails
 * - Webhook was already processed (idempotency)
 * - Subscription not found in database
 * - Stripe API call fails
 * - RevenueCat sync fails
 *
 * @since 1.0.0
 */
export class SubscriptionError extends Schema.TaggedError<SubscriptionError>()(
  "SubscriptionError",
  {
    code: SubscriptionErrorCode,
    message: Schema.String,
  },
) {}

/**
 * Why a per-seat billing mutation on the customer's subscription could not be
 * completed.
 *
 *  - `no_subscription` — the customer has no local subscription row, or the row
 *    carries no `stripeSubscriptionId`, so there is nothing to mutate.
 *  - `unmigratable_subscription` — the live Stripe subscription is not the safe
 *    shape this flow assumes (exactly one recurring item at quantity 1 for a
 *    legacy migration, or exactly one item at all). Surfaced for operator
 *    escalation rather than attempting silent surgery on an unexpected shape.
 *  - `payment_failed` — the off-session proration charge for the added seat
 *    could not be collected (declined / incomplete). The subscription quantity
 *    is left unchanged by Stripe's pending-update model.
 *  - `last_agent` — removing the last seat (floor quantity 1) is a
 *    subscription-cancel decision handled elsewhere, out of scope for a seat
 *    decrement.
 *
 * @since 1.0.0
 */
export const SeatBillingErrorReason = Schema.Literal(
  "no_subscription",
  "unmigratable_subscription",
  "payment_failed",
  "last_agent",
);
export type SeatBillingErrorReason = Schema.Schema.Type<typeof SeatBillingErrorReason>;

/**
 * Raised by `SeatBillingService` when a prorated seat add/remove on the
 * customer's single Stripe subscription cannot be completed. Carries a
 * machine-readable {@link SeatBillingErrorReason} so callers (T3 self-serve /
 * T4 admin) can distinguish an SCA/decline from an unmigratable-shape
 * escalation from a floor violation.
 *
 * @since 1.0.0
 */
export class SeatBillingError extends Schema.TaggedError<SeatBillingError>()(
  "SeatBillingError",
  {
    reason: SeatBillingErrorReason,
    message: Schema.String,
  },
) {}

// ============================================================================
// Slack Notification Errors
// ============================================================================

/**
 * Slack notification error code types.
 *
 * @since 1.0.0
 */
export const SlackNotificationErrorCode = Schema.Literal(
  "webhook_request_failed",
  "webhook_timeout",
  "config_missing",
  "invalid_response",
);
export type SlackNotificationErrorCode = Schema.Schema.Type<typeof SlackNotificationErrorCode>;

/**
 * Slack notification error for notification-related failures.
 *
 * Used when:
 * - Webhook HTTP request fails
 * - Webhook request times out
 * - Configuration is missing (SLACK_WEBHOOK_URL)
 * - Slack API returns invalid response
 *
 * @since 1.0.0
 */
export class SlackNotificationError extends Schema.TaggedError<SlackNotificationError>()(
  "SlackNotificationError",
  {
    code: SlackNotificationErrorCode,
    message: Schema.String,
  },
) {}

// ============================================================================
// SMS Notification Errors
// ============================================================================

/**
 * SMS notification error code types.
 *
 * - `not_configured`: one or more Twilio values (SID / auth token / from
 *   number) is absent, so no HTTP call was attempted.
 * - `request_failed`: the HTTP transport itself failed (network / DNS / TLS).
 * - `rejected`: Twilio answered with a non-2xx status and an error body.
 * - `invalid_response`: the Twilio response body could not be read or decoded.
 *
 * @since 1.0.0
 */
export const SmsNotificationErrorCode = Schema.Literal(
  "not_configured",
  "request_failed",
  "rejected",
  "invalid_response",
);
export type SmsNotificationErrorCode = Schema.Schema.Type<typeof SmsNotificationErrorCode>;

/**
 * SMS notification error for Twilio-backed SMS delivery failures. No message
 * ever embeds the auth token, the `Authorization` header, or the raw Twilio
 * response body.
 *
 * @since 1.0.0
 */
export class SmsNotificationError extends Schema.TaggedError<SmsNotificationError>()(
  "SmsNotificationError",
  {
    code: SmsNotificationErrorCode,
    message: Schema.String,
  },
) {}

// ============================================================================
// Email Delivery Errors
// ============================================================================

/**
 * Raised when a transactional email (e.g. the reseller start invite) cannot be
 * dispatched through AgentMail. The underlying {@link AgentMailError} is mapped
 * onto this RPC-layer error so the failure crosses the wire as a typed value.
 *
 * @since 1.0.0
 */
export class EmailDeliveryError extends Schema.TaggedError<EmailDeliveryError>()(
  "EmailDeliveryError",
  {
    message: Schema.String,
  },
) {}

// ============================================================================
// Valkey Errors
// ============================================================================

/**
 * Valkey error for Redis/Valkey operation failures.
 *
 * Used when:
 * - Valkey connection fails
 * - Heartbeat key read fails
 *
 * @since 1.0.0
 */
export class ValkeyError extends Schema.TaggedError<ValkeyError>()(
  "ValkeyError",
  {
    message: Schema.String,
  },
) {}

// ============================================================================
// Backup Errors
// ============================================================================

/**
 * Backup-credential wire errors raised by `mgmt.fetchBackupCredentials`.
 * The classes live in `@fm/cluster/services/backup-creds-schema` (CLA-555) —
 * the AWS-SDK-free split of `backup-creds` — so re-exporting them here keeps
 * `rpc/errors.ts` client-safe (client bundles must not pull `@aws-sdk/client-sts`).
 *
 * @since 1.0.0
 */
export { BackupCredentialMintFailed, BackupStorageNotConfigured } from "@fm/cluster/services/backup-creds-schema";

// ============================================================================
// Crypto Errors
// ============================================================================

/**
 * Crypto error for encryption/decryption failures.
 *
 * Used when:
 * - Encryption fails (invalid key, buffer errors)
 * - Decryption fails (wrong key, tampered data, invalid format)
 * - Key rotation fails during re-encryption
 *
 * @since 1.0.0
 */
export class CryptoError extends Schema.TaggedError<CryptoError>()(
  "CryptoError",
  {
    message: Schema.String,
    operation: Schema.Literal("encrypt", "decrypt", "rotate"),
  },
) {}

// ============================================================================
// Reseller Agreement Errors (CLA-585)
// ============================================================================

/**
 * The reseller-agreement object bucket is not configured yet — the
 * `RESELLER_AGREEMENTS_R2_*` env vars are unset (or the bucket is blank). The
 * config is resolved lazily per request, so the API boots fine and only the
 * agreement RPCs fail with this until ops provisions the bucket.
 *
 * @since 1.0.0
 */
export class AgreementStorageNotConfigured
  extends Schema.TaggedError<AgreementStorageNotConfigured>()("AgreementStorageNotConfigured", {
    message: Schema.String,
  })
{}

/**
 * An executed-agreement object-storage operation (upload / presign) failed.
 *
 * @since 1.0.0
 */
export class AgreementStorageError extends Schema.TaggedError<AgreementStorageError>()(
  "AgreementStorageError",
  {
    message: Schema.String,
    operation: Schema.Literal("upload", "getDownloadUrl"),
  },
) {}

/**
 * Baking the executed-agreement IR into PDF bytes failed (e.g. an unencodable
 * glyph).
 *
 * @since 1.0.0
 */
export class AgreementPdfError extends Schema.TaggedError<AgreementPdfError>()(
  "AgreementPdfError",
  {
    message: Schema.String,
  },
) {}

/**
 * A reseller-agreement-gated action (Stripe Connect ensure-account /
 * onboarding-link) was attempted before the caller's reseller accepted the
 * current agreement version. `version` is the current agreement version the
 * caller must accept.
 *
 * @since 1.0.0
 */
export class AgreementNotAccepted extends Schema.TaggedError<AgreementNotAccepted>()(
  "AgreementNotAccepted",
  {
    message: Schema.String,
    version: Schema.String,
  },
) {}
